import React, { useMemo } from "react";
import { getTransform, parsePresetPixelSize } from "../../../../../utils/getTransform";
import {
  aspectFromSize,
  thumbSizeFromAspect,
  useImageNaturalSize,
} from "../../../lib/toolPrepImageLayout";
import "./ResizeModeCards.css";

const FALLBACK_ASPECT = 3 / 4;
/** Fixed thumb width; height follows preset aspect ratio. */
const THUMB_WIDTH_PX = 140;

/**
 * Fit / Fill mode pickers — card frame uses preset AR; image uses same
 * getTransform as ResizePreview / CanvasEditor.
 */
const ResizeModeCards = ({
  resizeMode = "fit",
  onModeChange = () => {},
  imageUrl = null,
  preset = null,
  className = "",
}) => {
  const naturalSize = useImageNaturalSize(imageUrl);

  const parsed = useMemo(() => parsePresetPixelSize(preset), [preset]);
  const aspect = useMemo(() => {
    if (parsed?.aspectRatio) return parsed.aspectRatio;
    const fromPreset = aspectFromSize(preset?.width, preset?.height, 0);
    if (fromPreset > 0) return fromPreset;
    return aspectFromSize(
      naturalSize.width,
      naturalSize.height,
      FALLBACK_ASPECT
    );
  }, [parsed, preset, naturalSize]);

  const thumbSize = useMemo(
    () => thumbSizeFromAspect(aspect, THUMB_WIDTH_PX, FALLBACK_ASPECT),
    [aspect]
  );

  const fitTransform = useMemo(
    () =>
      getTransform(
        naturalSize.width,
        naturalSize.height,
        thumbSize.width,
        thumbSize.height,
        "fit"
      ),
    [naturalSize, thumbSize]
  );

  const fillTransform = useMemo(
    () =>
      getTransform(
        naturalSize.width,
        naturalSize.height,
        thumbSize.width,
        thumbSize.height,
        "fill"
      ),
    [naturalSize, thumbSize]
  );

  const renderThumb = (mode, transform) => (
    <div
      className={`resize-mode-thumb resize-mode-thumb--${mode}`}
      style={{
        width: thumbSize.width,
        height: thumbSize.height,
        aspectRatio: `${thumbSize.aspect}`,
      }}
    >
      {imageUrl ? (
        <img
          src={imageUrl}
          alt=""
          className={
            transform.width > 0
              ? "resize-mode-thumb__img"
              : "resize-mode-thumb__img resize-mode-thumb__img--fallback"
          }
          style={
            transform.width > 0
              ? {
                  width: transform.width,
                  height: transform.height,
                  left: transform.x,
                  top: transform.y,
                }
              : undefined
          }
          draggable={false}
        />
      ) : null}
    </div>
  );

  return (
    <div className={`resize-mode-grid${className ? ` ${className}` : ""}`}>
      <button
        type="button"
        className={`resize-mode-card${resizeMode === "fit" ? " resize-mode-card--active" : ""}`}
        onClick={() => onModeChange("fit")}
      >
        <div className="resize-mode-frame">
          {renderThumb("fit", fitTransform)}
        </div>
        <span className="resize-mode-label">Fit</span>
      </button>
      <button
        type="button"
        className={`resize-mode-card${resizeMode === "fill" ? " resize-mode-card--active" : ""}`}
        onClick={() => onModeChange("fill")}
      >
        <div className="resize-mode-frame">
          {renderThumb("fill", fillTransform)}
        </div>
        <span className="resize-mode-label">Fill</span>
      </button>
    </div>
  );
};

export default ResizeModeCards;
